import type { Dispatch } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { DmBtnPrimary, DmBtnSecondary } from '../DmFormPrimitives';
import type { AttachmentNotice } from '../composer/useComposerAttachment';
import { hasMarks, type MarkupAction, type MarkupState } from './markupHistory';
import { MarkupStage } from './MarkupStage';
import { MarkupToolRail } from './MarkupToolRail';
import { MarkupTopBar } from './MarkupTopBar';
import type { MarkupPhoto } from './scanPhotoOptions';
import { useMarkupAttach, type MarkupAttachState } from './useMarkupAttach';
import { useMarkupEditor } from './useMarkupEditor';
import { useScanPhotoBitmap, type ScanPhotoBitmap } from './useScanPhotoBitmap';

interface ScanMarkupEditorProps {
    photo: MarkupPhoto;
    markup: MarkupState;
    dispatch: Dispatch<MarkupAction>;
    notice: AttachmentNotice | null;
    // Off while another dialog is stacked on top, so its keys don't edit the marks.
    shortcutsEnabled: boolean;
    onBack: () => void;
    onAttach: (jpeg: Blob) => boolean;
}

const REJECTED_FALLBACK = 'This image could not be attached.';

function attachProblem(state: MarkupAttachState, notice: AttachmentNotice | null): string | null {
    if (state.status === 'failed') return state.message;
    if (state.status === 'rejected') return notice?.text ?? REJECTED_FALLBACK;
    return null;
}

function attachLabel(working: boolean, marked: boolean): string {
    if (working) return 'Attaching…';
    return marked ? 'Attach to message' : 'Attach without marks';
}

function StageMessage({ photo, onRetry }: { photo: ScanPhotoBitmap; onRetry: () => void }) {
    if (photo.status === 'loading') {
        return (
            <div role="status" className="flex flex-col items-center gap-3 text-sm text-gray-500">
                <span className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                Loading photo…
            </div>
        );
    }
    if (photo.status !== 'error') return null;
    return (
        <div
            role="alert"
            className="max-w-sm text-center text-sm text-gray-600 flex flex-col items-center gap-2"
        >
            {photo.message}
            <button
                type="button"
                onClick={onRetry}
                className="font-semibold text-gray-900 underline"
            >
                Try again
            </button>
        </div>
    );
}

function ScanMarkupEditor({
    photo,
    markup,
    dispatch,
    notice,
    shortcutsEnabled,
    onBack,
    onAttach
}: ScanMarkupEditorProps) {
    const { photo: loaded, retry } = useScanPhotoBitmap(photo.fullUrl);
    const image = loaded.status === 'ready' ? loaded.bitmap : null;
    const editor = useMarkupEditor({ markup, dispatch, image, shortcutsEnabled });
    const { state: attachState, attach } = useMarkupAttach(image, markup.shapes, onAttach);
    const problem = attachProblem(attachState, notice);
    const working = attachState.status === 'working';

    return (
        <>
            <div className="flex flex-1 min-h-0">
                <MarkupToolRail tool={editor.tool} onToolChange={editor.setTool} />
                <div className="flex flex-1 min-w-0 flex-col">
                    <MarkupTopBar
                        tool={editor.tool}
                        selectedKind={editor.selected?.kind ?? null}
                        color={editor.color}
                        onColorChange={editor.changeColor}
                        size={editor.size}
                        onSizeChange={editor.changeSize}
                        history={editor.history}
                        onUndo={editor.undo}
                        onRedo={editor.redo}
                        onClear={editor.clear}
                    />
                    {image ? (
                        <MarkupStage
                            image={image}
                            imageLabel={photo.label}
                            shapes={markup.shapes}
                            editor={editor}
                            dispatch={dispatch}
                        />
                    ) : (
                        <div className="flex-1 min-h-0 bg-gray-100 flex items-center justify-center">
                            <StageMessage photo={loaded} onRetry={retry} />
                        </div>
                    )}
                </div>
            </div>
            <footer className="flex items-center gap-3 px-6 py-3 border-t border-gray-200">
                <DmBtnSecondary onClick={onBack} disabled={working}>
                    <span className="inline-flex items-center gap-1.5">
                        <ChevronLeftIcon className="w-4 h-4" />
                        Back to photos
                    </span>
                </DmBtnSecondary>
                <p role="alert" className="flex-1 min-w-0 text-right text-sm text-red-600">
                    {problem}
                </p>
                <DmBtnPrimary onClick={() => void attach()} disabled={!image || working}>
                    {attachLabel(working, hasMarks(markup))}
                </DmBtnPrimary>
            </footer>
        </>
    );
}

export { ScanMarkupEditor };
